import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

const CLOUDFLARE_ACCESS_LOGOUT_URL = '/cdn-cgi/access/logout';
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

interface AuthContextValue {
  /** Whether the session is currently authenticated */
  isAuthenticated: boolean;
  /** Triggers logout: invalidates session and redirects to Cloudflare Access */
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: true,
  logout: () => {},
});

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

/**
 * AuthGuard wraps the application and provides session management.
 *
 * Responsibilities:
 * - Tracks user activity and detects 30-minute inactivity
 * - Intercepts 401 responses from the API to detect expired sessions
 * - Provides logout functionality via context
 * - On session invalidation, redirects to Cloudflare Access login
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const lastActivityRef = useRef<number>(Date.now());
  const sessionInvalidatedRef = useRef(false);

  // Update last activity timestamp on user interactions
  const recordActivity = useCallback(() => {
    if (sessionInvalidatedRef.current) return;

    const now = Date.now();
    const elapsed = now - lastActivityRef.current;

    // If user has been inactive for 30+ minutes, invalidate session on this interaction
    if (elapsed >= INACTIVITY_TIMEOUT_MS) {
      invalidateSession();
      return;
    }

    lastActivityRef.current = now;
  }, []);

  // Invalidate session and redirect to Cloudflare Access login
  const invalidateSession = useCallback(() => {
    if (sessionInvalidatedRef.current) return;
    sessionInvalidatedRef.current = true;
    setIsAuthenticated(false);
    window.location.href = CLOUDFLARE_ACCESS_LOGOUT_URL;
  }, []);

  // Logout handler exposed via context
  const logout = useCallback(() => {
    invalidateSession();
  }, [invalidateSession]);

  // Register activity listeners for inactivity tracking
  useEffect(() => {
    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const;

    for (const event of activityEvents) {
      document.addEventListener(event, recordActivity, { passive: true });
    }

    return () => {
      for (const event of activityEvents) {
        document.removeEventListener(event, recordActivity);
      }
    };
  }, [recordActivity]);

  // Install global fetch interceptor to detect 401 responses
  useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
      const response = await originalFetch(...args);

      if (response.status === 401 && !sessionInvalidatedRef.current) {
        invalidateSession();
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [invalidateSession]);

  const contextValue: AuthContextValue = {
    isAuthenticated,
    logout,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}
